import React, { useState } from 'react';
import axios from 'axios';
import { Croissant, Sandwich, Beef, ChefHat, Cookie, CakeSlice, Pizza, Check, UtensilsCrossed } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { images, customCakes as defCustomCakes } from '../mock/mock';
import { useContent } from '../context/ContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cateringItems = [
  { name: 'Plain Croissants', icon: Croissant, desc: 'Buttery, flaky and baked fresh, perfect by the dozen.', img: 'https://images.unsplash.com/photo-1623334044303-241021148842?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Filled Savoury Croissants', icon: Sandwich, desc: 'Generously filled with seasonal savoury combinations.', img: 'https://images.unsplash.com/photo-1639667852145-466e29aa49fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Sausage Rolls', icon: Beef, desc: 'Golden, hand-rolled and always a crowd favourite.', img: 'https://images.unsplash.com/photo-1608582037152-adefa9decb70?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Pies', icon: ChefHat, desc: 'Hearty house-made pies with rich, slow-cooked fillings.', img: 'https://images.unsplash.com/photo-1545668855-b923f0176935?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Cookies', icon: Cookie, desc: 'Chewy, wholesome cookies made in small batches.', img: 'https://images.unsplash.com/photo-1583743089695-4b816a340f82?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Brownies / Slices', icon: CakeSlice, desc: 'Decadent brownies and rotating seasonal slices.', img: 'https://images.unsplash.com/photo-1515037893149-de7f840978e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { name: 'Focaccia Varieties', icon: Pizza, desc: 'Pillowy focaccia in a range of toppings and flavours.', img: 'https://images.unsplash.com/photo-1767065886868-c4ec499606a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
];

const cakeChecklist = [
  { group: 'Occasion', options: ['Birthday', 'Wedding', 'Engagement', 'Anniversary', 'Baby shower', 'Corporate / event', 'Other'] },
  { group: 'Flavour', options: ['Vanilla', 'Chocolate', 'Red velvet', 'Carrot', 'Lemon', 'Pistachio', 'Tiramisu'] },
  { group: 'Dietary', options: ['Gluten free', 'Vegan', 'Dairy free', 'Nut free', 'Egg free', 'Refined-sugar free'] },
  { group: 'Finishing touches', options: ['Custom message / topper', 'Fresh flowers', 'Multi-tier', 'Printed photo / image', 'Specific colour theme'] },
];

export default function Catering() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', date: '', guests: '', notes: '' });
  const [qty, setQty] = useState({});
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cake, setCake] = useState({ name: '', email: '', phone: '', date: '', servings: '', details: '' });
  const [cakeOpts, setCakeOpts] = useState({});
  const [cakeSent, setCakeSent] = useState(false);
  const [cakeBusy, setCakeBusy] = useState(false);
  useReveal([]);
  const { txt, content } = useContent();
  const items = content.catering && content.catering.length ? content.catering : cateringItems;
  const cc = (content.customCakes && content.customCakes.checklist) ? content.customCakes : defCustomCakes;
  const ccChecklist = cc.checklist || cakeChecklist;

  const setQuantity = (name, val) => setQty((q) => ({ ...q, [name]: val }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);

    const selected = items
      .filter((i) => Number(qty[i.name]) > 0)
      .map((i) => `${i.name}: ${qty[i.name]}`);

    const message = [
      selected.length ? `Items requested:\n${selected.join('\n')}` : 'No specific quantities selected.',
      form.date ? `Event date: ${form.date}` : '',
      form.guests ? `Guests: ${form.guests}` : '',
      form.phone ? `Phone: ${form.phone}` : '',
      form.notes ? `Notes: ${form.notes}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        business: form.phone,
        message,
        type: 'catering',
      });
      setSent(true);
      setForm({ name: '', email: '', phone: '', date: '', guests: '', notes: '' });
      setQty({});
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('catering enquiry failed', err);
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const toggleCakeOpt = (val) => setCakeOpts((o) => ({ ...o, [val]: !o[val] }));

  const submitCake = async (e) => {
    e.preventDefault();
    if (cakeBusy) return;
    setCakeBusy(true);
    const selected = Object.keys(cakeOpts).filter((k) => cakeOpts[k]);
    const message = [
      'CUSTOM CAKE ENQUIRY',
      selected.length ? `Customisations: ${selected.join(', ')}` : '',
      cake.servings ? `Servings / size: ${cake.servings}` : '',
      cake.date ? `Needed by: ${cake.date}` : '',
      cake.phone ? `Phone: ${cake.phone}` : '',
      cake.details ? `Details: ${cake.details}` : '',
    ].filter(Boolean).join('\n');
    try {
      await axios.post(`${API}/contact`, {
        name: cake.name,
        email: cake.email,
        business: cake.phone,
        message,
        type: 'custom-cake',
      });
      setCakeSent(true);
      setCake({ name: '', email: '', phone: '', date: '', servings: '', details: '' });
      setCakeOpts({});
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('custom cake enquiry failed', err);
      setCakeSent(true);
    } finally {
      setCakeBusy(false);
    }
  };

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      {/* hero, hidden; remove the spacer above and "hidden" below to reinstate */}
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
        <img
          src={images.cafeInterior}
          alt="Al Naturale catering"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">Catering</span>
          <h1 className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            Catering for every occasion
          </h1>
          <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
            From office mornings to celebrations, freshly baked, made with the same
            natural care as everything we do.
          </p>
        </div>
      </section>

      {/* catering menu */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal max-w-2xl">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] leading-tight">
              {txt('catering', 'menuHeading', 'Our catering menu')}
            </h2>
            <p className="mt-5 text-[#2C3A28] text-lg leading-relaxed">
              {txt('catering', 'menuIntro', 'Available in platters and by the dozen. Mix and match to suit your event.')}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((i) => {
              const Icon = i.icon || UtensilsCrossed;
              return (
              <div
                key={i.name}
                className="reveal rounded-3xl bg-[#EDEAE0] overflow-hidden"
                data-testid={`catering-item-${i.name.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
              >
                <div className="relative h-44">
                  <img src={i.img} alt={i.name} loading="lazy" className="w-full h-full object-cover" />
                  <span className="absolute -bottom-5 left-6 w-12 h-12 rounded-full bg-[#2C3A28] text-[#E9EBED] flex items-center justify-center ring-4 ring-[#EDEAE0]">
                    <Icon size={20} />
                  </span>
                </div>
                <div className="px-7 pt-8 pb-7">
                  <h3 className="font-display text-2xl font-semibold text-[#2C3A28]">{i.name}</h3>
                  <p className="mt-2 text-[#2C3A28] leading-relaxed">{i.desc}</p>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* enquiry / order form */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="reveal rounded-[2rem] bg-[#2C3A28] text-[#EDEAE0] px-7 sm:px-12 py-12 grain">
            <div className="flex items-center gap-3">
              <UtensilsCrossed size={30} className="text-[#EDEAE0]" />
              <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">{txt('catering', 'enquiryHeading', 'Catering enquiry / order')}</h2>
            </div>
            <p className="mt-4 max-w-2xl text-[#EDEAE0]/85 text-lg leading-relaxed">
              {txt('catering', 'enquiryBody', "Add quantities for anything you'd like and share your event details, we'll confirm availability, pricing and pick-up or delivery.")}
            </p>

            {sent ? (
              <div className="mt-8 rounded-2xl bg-[#E9EBED] text-[#2C3A28] p-8 text-center" data-testid="catering-success">
                <div className="mx-auto w-12 h-12 rounded-full bg-[#2C3A28] text-[#E9EBED] flex items-center justify-center">
                  <Check size={24} />
                </div>
                <p className="mt-4 font-display text-2xl">Grazie!</p>
                <p className="mt-1 text-[#3B2A20]/70">
                  Thanks for your catering enquiry, we'll be in touch shortly to confirm the details.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="catering-send-another"
                  className="mt-5 text-sm font-semibold text-[#8a6d3b] hover:text-[#2C3A28]"
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-6" data-testid="catering-form">
                {/* quantities */}
                <div className="rounded-2xl bg-[#E9EBED]/[0.06] border border-[#E9EBED]/10 p-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#EDEAE0]">Items</p>
                  <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    {items.map((i) => (
                      <div key={i.name} className="flex items-center justify-between gap-3 rounded-xl bg-[#E9EBED]/[0.05] px-4 py-2.5">
                        <span className="text-sm text-[#EDEAE0]/90">{i.name}</span>
                        <input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          value={qty[i.name] || ''}
                          onChange={(e) => setQuantity(i.name, e.target.value)}
                          placeholder="Qty"
                          data-testid={`catering-qty-${i.name.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
                          className="w-20 rounded-lg bg-[#E9EBED] text-[#2C3A28] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2C3A28]"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* details */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="catering-name"
                    className="w-full rounded-xl bg-[#E9EBED] px-4 py-3 text-[#2C3A28] outline-none focus:ring-2 focus:ring-[#2C3A28]"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address"
                    data-testid="catering-email"
                    className="w-full rounded-xl bg-[#E9EBED] px-4 py-3 text-[#2C3A28] outline-none focus:ring-2 focus:ring-[#2C3A28]"
                  />
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone (optional)"
                    data-testid="catering-phone"
                    className="w-full rounded-xl bg-[#E9EBED] px-4 py-3 text-[#2C3A28] outline-none focus:ring-2 focus:ring-[#2C3A28]"
                  />
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="catering-date"
                    className="w-full rounded-xl bg-[#E9EBED] px-4 py-3 text-[#2C3A28] outline-none focus:ring-2 focus:ring-[#2C3A28]"
                  />
                  <input
                    value={form.guests}
                    onChange={(e) => setForm({ ...form, guests: e.target.value })}
                    placeholder="Number of guests (optional)"
                    data-testid="catering-guests"
                    className="w-full rounded-xl bg-[#E9EBED] px-4 py-3 text-[#2C3A28] outline-none focus:ring-2 focus:ring-[#2C3A28] sm:col-span-2"
                  />
                </div>

                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything else we should know? (dietaries, delivery, timing...)"
                  data-testid="catering-notes"
                  className="w-full rounded-xl bg-[#E9EBED] px-4 py-3 text-[#2C3A28] outline-none focus:ring-2 focus:ring-[#2C3A28] resize-none"
                />

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="catering-submit"
                  className="w-full sm:w-auto rounded-full bg-[#EDEAE0] text-[#2C3A28] px-10 py-4 font-semibold hover:bg-white transition-colors disabled:opacity-60"
                >
                  {busy ? 'Sending...' : 'Send catering enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CUSTOM CAKES */}
      <section className="pb-24" data-testid="custom-cakes-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              <CakeSlice size={16} /> Custom Cakes
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] leading-tight">
              {cc.heading || 'Custom cakes, made to order'}
            </h2>
            <p className="mt-4 text-[#2C3A28] text-lg leading-relaxed">
              {cc.intro || "Tell us about your dream cake and we'll craft something special. Tick anything that applies and share the details below."}
            </p>
          </div>

          {cakeSent ? (
            <div className="reveal mt-10 rounded-2xl bg-white text-[#2C3A28] p-8 text-center" data-testid="custom-cake-success">
              <div className="mx-auto w-12 h-12 rounded-full bg-[#2C3A28] text-[#E9EBED] flex items-center justify-center">
                <Check size={24} />
              </div>
              <p className="mt-4 font-display text-2xl">Grazie!</p>
              <p className="mt-1 text-[#3B2A20]/70">
                Thanks for your custom cake enquiry, we'll be in touch shortly to discuss the details.
              </p>
              <button
                onClick={() => setCakeSent(false)}
                data-testid="custom-cake-send-another"
                className="mt-5 text-sm font-semibold text-[#8a6d3b] hover:text-[#2C3A28]"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={submitCake} className="reveal mt-10" data-testid="custom-cake-form">
              <div className="rounded-[2rem] border-2 border-[#D2B489]/50 bg-white/70 p-6 sm:p-9 shadow-[0_18px_50px_-20px_rgba(44,58,40,0.35)]">
              {/* detailed checklist */}
              <div className="grid sm:grid-cols-2 gap-5" data-testid="custom-cake-checklist">
                {ccChecklist.map((grp) => (
                  <div key={grp.group} className="rounded-2xl bg-[#EDEAE0] p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#2C3A28]">{grp.group}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {grp.options.map((opt) => {
                        const active = !!cakeOpts[opt];
                        return (
                          <button
                            type="button"
                            key={opt}
                            onClick={() => toggleCakeOpt(opt)}
                            data-testid={`cake-opt-${opt.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
                            aria-pressed={active}
                            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                              active
                                ? 'bg-[#2C3A28] text-[#E9E5D8] border-[#2C3A28]'
                                : 'bg-white text-[#2C3A28] border-transparent hover:border-[#2C3A28]/30'
                            }`}
                          >
                            {active && <Check size={14} />} {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* details */}
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <input
                  required
                  value={cake.name}
                  onChange={(e) => setCake({ ...cake, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="custom-cake-name"
                  className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                />
                <input
                  required
                  type="email"
                  value={cake.email}
                  onChange={(e) => setCake({ ...cake, email: e.target.value })}
                  placeholder="Email address"
                  data-testid="custom-cake-email"
                  className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                />
                <input
                  value={cake.phone}
                  onChange={(e) => setCake({ ...cake, phone: e.target.value })}
                  placeholder="Phone (optional)"
                  data-testid="custom-cake-phone"
                  className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                />
                <input
                  type="date"
                  value={cake.date}
                  onChange={(e) => setCake({ ...cake, date: e.target.value })}
                  data-testid="custom-cake-date"
                  className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                />
                <input
                  value={cake.servings}
                  onChange={(e) => setCake({ ...cake, servings: e.target.value })}
                  placeholder="Servings / size (e.g. 20 people)"
                  data-testid="custom-cake-servings"
                  className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors sm:col-span-2"
                />
                <textarea
                  rows={3}
                  value={cake.details}
                  onChange={(e) => setCake({ ...cake, details: e.target.value })}
                  placeholder="Tell us more about your cake (design, colours, inscription, inspiration...)"
                  data-testid="custom-cake-details"
                  className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors resize-none sm:col-span-2"
                />
              </div>

              <button
                type="submit"
                disabled={cakeBusy}
                data-testid="custom-cake-submit"
                className="mt-6 w-full sm:w-auto rounded-full bg-[#2C3A28] text-[#EDEAE0] px-10 py-4 font-semibold hover:bg-[#1f2a1c] transition-colors disabled:opacity-60"
              >
                {cakeBusy ? 'Sending...' : 'Send custom cake enquiry'}
              </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
